/**
 * Admin 「외부 연동」 — K-MRS 약사 접근코드 조회·발급·폐기 훅.
 *
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — 화면 설계 A 7
 *
 * 조회는 staleTime 0(전역 5분을 끈다) — 다른 관리자가 그새 폐기·발급했는지 바로 보여야 한다.
 * 발급·폐기는 성공·실패 모두(onSettled) 코드 목록과 기관 목록을 다시 불러온다. 실패 사유
 * (`PartnerAdminError.message`)는 토스트로 보여준다. 화면은 `mutateAsync`로 성공 여부를 받아
 * 원문 창을 열거나 폐기 확인 창을 닫는다.
 */
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ToastManager } from "components/organisms/CommonUI";
import {
  issueKmrsAccessCode,
  listKmrsAccessCodes,
  listKmrsOrganizations,
  revealKmrsAccessCode,
  revokeKmrsAccessCode,
  type AdminAccessCode,
  type AdminOrganization,
  type IssueAccessCodeBody,
  type RevealAccessCodeResult,
} from "api/kmrsAdminService";

export const kmrsAdminQueryKeys = {
  all: ["kmrsAdmin"] as const,
  accessCodes: ["kmrsAdmin", "accessCodes"] as const,
  organizations: ["kmrsAdmin", "organizations"] as const,
};

const toastError = (err: unknown): void => {
  const content = err instanceof Error && err.message ? err.message : "요청을 처리하지 못했어요.";
  ToastManager.show({ content, duration: 4000 });
};

/** 접근코드 목록 — includeRevoked=true로 한 번 받아 활성/폐기를 화면에서 나눈다. */
export const useKmrsAccessCodes = () =>
  useQuery<{ items: AdminAccessCode[] }, Error>(
    kmrsAdminQueryKeys.accessCodes,
    () => listKmrsAccessCodes(true),
    { staleTime: 0 }
  );

/**
 * 기관 목록 — 발급 창의 프리셋에서만 쓴다. 창(컴포넌트)이 직접 구독한다(화면 설계 A 7의 §2 예외).
 * `enabled`로 창이 열렸을 때만 켠다.
 */
export const useKmrsOrganizations = (enabled = true) =>
  useQuery<{ items: AdminOrganization[] }, Error>(
    kmrsAdminQueryKeys.organizations,
    listKmrsOrganizations,
    { staleTime: 0, enabled }
  );

/**
 * 발급·폐기·원문 재열람. 발급·폐기는 성공·실패 모두 코드·기관 목록을 다시 불러온다(onSettled).
 * 원문 재열람(reveal, D7a)은 상태를 바꾸지 않으므로 재조회하지 않는다 — 실패 사유(409 등)만 토스트로
 * 보여준다. 성공 원문은 화면이 mutateAsync로 받아 클립보드에 넣거나 행 안에 펼친다(저장·로그 금지).
 */
export const useKmrsAccessCodeMutations = () => {
  const qc = useQueryClient();
  const invalidateAll = () => {
    qc.invalidateQueries(kmrsAdminQueryKeys.accessCodes);
    qc.invalidateQueries(kmrsAdminQueryKeys.organizations);
  };
  return {
    // 발급 결과(원문)는 화면이 mutateAsync로 받아 원문 창을 연다 — 완료 토스트는 띄우지 않는다.
    issue: useMutation((body: IssueAccessCodeBody) => issueKmrsAccessCode(body), {
      onSettled: invalidateAll,
      onError: toastError,
    }),
    revoke: useMutation((id: string) => revokeKmrsAccessCode(id), {
      onSettled: invalidateAll,
      onError: toastError,
    }),
    // reveal: 재조회 없음. 실패(원문 없음 409·없는 코드 404 등) 사유만 토스트.
    reveal: useMutation<RevealAccessCodeResult, unknown, string>((id) => revealKmrsAccessCode(id), {
      onError: toastError,
    }),
  };
};
